import { A } from '@ember/array';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { restartableTask, type TaskInstance } from 'ember-concurrency';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type { FilterItem } from 'garaje/models/common';
import type { Field } from 'garaje/models/location';
import type PersonAccessEventGroupModel from 'garaje/models/person-access-event-group';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { DATE_FNS_YYYY_MM_DD, modifyDateInTimeZone, parseYyyyMmDdInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { toIntegrationDisplayNames, toIntegrationTypeNames } from 'garaje/utils/integration-name-mapping';
import { reads } from 'macro-decorators';
import { resolve } from 'rsvp';

const HEADER_ENUMS = {
  name: 'Name',
  autoSignInStatus: 'Auto sign-in status',
  entranceName: 'Entrance Name',
  integrationName: 'Integration Name',
  eventTimeHeader: 'Event Time',
  receivedAtHeader: 'Received at',
  date: 'Date',
  eventType: 'Event Type',
  ssidName: 'SSID Name',
  signInLocation: 'Location',
  ipSubnet: 'IP subnet',
};

interface AccessEventTableArgs {
  query: string;
  startDate: string;
  endDate: string;
  date: string;
  setTotalAccessEvents: (count: number) => void;
  reset: boolean;
  initializeReset: () => void;
  activityTypes?: string;
  fieldOptions?: Field[];
  outcomes?: FilterItem[];
  integrationNames?: string[];
  onNewIntegrationNames?: (names: string[]) => void;
}

interface AccessEventQueryParams {
  'location-ids': string;
  'start-time'?: string;
  'end-time'?: string;
  query?: string;
  employee?: string;
  'activity-types'?: string;
  outcomes?: string;
  'integration-names'?: string;
}

interface SortableField extends Field {
  sort?: string;
  tooltip?: string;
}

const limit = 25;

export default class AccessEventTable extends Component<AccessEventTableArgs> {
  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare store: StoreService;
  @service declare state: StateService;
  @service declare metrics: MetricsService;
  @tracked personGroups: PersonAccessEventGroupModel[] = [];
  @tracked expandedPersonIds: Set<string> = new Set();
  @tracked sort: string = '-event-timestamp';
  @tracked hasLoadError = false;
  page = 0;
  totalAccessEvents!: number;
  totalGroupCount!: number;

  @reads('currentLocation.timezone', 'America/Los_Angeles') timezone!: string;

  primaryHeaders = [
    {
      name: HEADER_ENUMS.name,
      componentName: 'custom-column',
      sort: 'person-name',
      tooltip: 'Person who triggered the access event',
    },
    {
      name: HEADER_ENUMS.entranceName,
      componentName: 'custom-column',
      tooltip: 'Door or entry point that was accessed',
    },
    {
      name: HEADER_ENUMS.integrationName,
      componentName: 'custom-column',
      tooltip: 'Integration that reported the event',
    },
    {
      name: HEADER_ENUMS.eventTimeHeader,
      componentName: 'custom-column',
      sort: 'event-timestamp',
      tooltip: 'When the access event actually occurred',
    },
    {
      name: HEADER_ENUMS.receivedAtHeader,
      componentName: 'custom-column',
      sort: 'received-at-timestamp',
      tooltip: 'When event was received by our services',
    },
    { name: HEADER_ENUMS.eventType, componentName: 'custom-column', tooltip: 'Type of access event' },
    {
      name: HEADER_ENUMS.ssidName,
      componentName: 'custom-column',
      tooltip: 'WiFi network the device connected to (WiFi events only)',
    },
    {
      name: HEADER_ENUMS.signInLocation,
      componentName: 'custom-column',
      tooltip: 'City, state and country reported by the SSO provider (SSO events only)',
    },
    {
      name: HEADER_ENUMS.ipSubnet,
      componentName: 'custom-column',
      tooltip: 'First two octets of the source IP (SSO and MDM events only). Full IP is intentionally not retained.',
    },
  ];

  get dateWithDefault(): string {
    return this.args.date || formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): Date {
    if (this.args.startDate) return new Date(this.args.startDate);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);

    return start;
  }

  get endDateWithDefault(): Date {
    if (this.args.endDate) return new Date(this.args.endDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  accessEventParams(pageNumber: number): Record<string, unknown> {
    const { currentLocation } = this.state;
    const offset = (pageNumber - 1) * limit;
    const filter: AccessEventQueryParams = {
      'location-ids': currentLocation.id,
    };

    const { query, activityTypes, outcomes, integrationNames } = this.args;

    if (isPresent(query)) {
      filter.query = query;
    }

    if (isPresent(activityTypes)) {
      filter['activity-types'] = activityTypes;
    }

    if (outcomes && outcomes.length > 0) {
      filter['outcomes'] = outcomes.map((o) => o.filter).join(',');
    }

    if (integrationNames && integrationNames.length > 0) {
      filter['integration-names'] = toIntegrationTypeNames(integrationNames).join(',');
    }

    filter['start-time'] = formatISO(this.startDateWithDefault);
    filter['end-time'] = formatISO(this.endDateWithDefault);

    return {
      filter,
      page: { limit: limit, offset },
      sort: this.sort,
    };
  }

  get accessEventDashboardFields(): Field[] {
    const { currentLocation } = this.state;
    const dashboardFields = currentLocation.dashboardFields;
    return [...dashboardFields];
  }

  get hasMorePages(): boolean {
    return this.personGroups.length < this.totalGroupCount;
  }

  get fieldOptions(): Field[] {
    // If fieldOptions are passed in from parent, use those (allows for reactive updates)
    if (this.args.fieldOptions) {
      return this.args.fieldOptions;
    }

    // Otherwise, fall back to generating them locally (for backwards compatibility)
    const alwaysEnabledColumns = ['Name', 'Event Time', 'Event Type'];
    let headers: SortableField[] = [...this.primaryHeaders];
    headers = headers.map(({ name, componentName, sort, tooltip }) => {
      return {
        name,
        componentName,
        // always show alwaysEnabledColumns
        show: alwaysEnabledColumns.includes(name) || isPresent(A(this.accessEventDashboardFields).findBy('name', name)),
        // do not allow disabling of alwaysEnabledColumns
        disabled: alwaysEnabledColumns.includes(name),
        sort,
        tooltip,
      };
    });
    return headers;
  }

  get todayAsString(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get isToday(): boolean {
    const { dateWithDefault, startDateWithDefault, endDateWithDefault, todayAsString } = this;

    if (dateWithDefault !== todayAsString) return false;
    if (formatInTimeZone(startDateWithDefault, this.timezone, DATE_FNS_YYYY_MM_DD) !== todayAsString) return false;
    if (formatInTimeZone(endDateWithDefault, this.timezone, DATE_FNS_YYYY_MM_DD) !== todayAsString) return false;

    return true;
  }

  get expandedPersonIdsArray(): string[] {
    return Array.from(this.expandedPersonIds);
  }

  _loadMore(): void | Promise<void> | TaskInstance<void> {
    // Because of the way loadMore works -- there are scenarios where
    // this can be called if destroyed
    if (this.isDestroyed) {
      return;
    }
    if (this.hasMorePages) {
      return this.loadAccessEvents.perform();
    } else {
      return resolve();
    }
  }

  @action
  loadMore(): void | Promise<void> | TaskInstance<void> {
    if (this.loadAccessEvents.isRunning) {
      return;
    }
    return this._loadMore();
  }

  resetTable(): void {
    this.page = 0;
    this.personGroups = [];
    this.expandedPersonIds = new Set();
  }

  @action
  reset(): void {
    if (this.args.reset) {
      this.resetTable();
    }
  }

  get sortField(): string {
    return this.sort.replace(/^[-|+]/g, '');
  }

  get sortDirection(): string {
    return this.sort.startsWith('-') ? 'desc' : 'asc';
  }

  @action
  sortAccessEvents(field: string, direction: string): void {
    // set sort
    const dir = direction === 'asc' ? '' : '-';
    this.sort = `${dir}${field}`;
    this.metrics.trackEvent('Access Events Sorted', { sort_field_name: field });

    // reset the page before refetching sorted data
    this.resetTable();
    void this.loadAccessEvents.perform();
  }

  @action
  togglePersonGroup(groupId: string): void {
    if (this.expandedPersonIds.has(groupId)) {
      this.expandedPersonIds.delete(groupId);
    } else {
      this.expandedPersonIds.add(groupId);
    }
    this.expandedPersonIds = new Set(this.expandedPersonIds);
  }

  loadAccessEvents = restartableTask(async () => {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_LOADING_ACCESS_EVENTS');
    this.hasLoadError = false;
    try {
      if (this.page == 0 || this.args.reset) {
        this.resetTable();
      }
      const nextPage = this.page + 1;
      const params = this.accessEventParams(nextPage);

      type PersonGroupPage = PaginatedRecordArray<PersonAccessEventGroupModel> & {
        meta: { 'total-count': number; 'total-group-count': number };
      };
      const personGroups = <PersonGroupPage>await this.store.query('person-access-event-group', params);

      this.page = nextPage;
      if (this.args.reset) {
        this.args.initializeReset();
      }

      const newGroups = personGroups.slice();
      this.personGroups = [...this.personGroups, ...newGroups];

      if (this.args.onNewIntegrationNames) {
        const types = newGroups.flatMap((g) => g.integrationTypes).filter(Boolean);
        const names = [...new Set(toIntegrationDisplayNames(types))];
        if (names.length > 0) {
          this.args.onNewIntegrationNames(names);
        }
      }

      if (personGroups.meta) {
        this.totalGroupCount = personGroups.meta?.['total-group-count'] ?? 0;
        this.totalAccessEvents = personGroups.meta?.['total-count'] ?? 0;
        this.args.setTotalAccessEvents(this.totalAccessEvents);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log({ e });
      this.hasLoadError = true;
      this.page = 0;
    }
  });
}
